// Browser rendering of the invitation design format (see docs/INVITATION_DESIGN_SPEC.md).
// The server renderer (backend/app/core/invitation_render.py) implements the same rules,
// so what the editor shows is what the paid download looks like.

export const PAGE_WIDTH = 816; // base units (US Letter @ 96 DPI)
export const PAGE_HEIGHT = 1056;
export const PAGE_ASPECT = '8.5 / 11';

const HEBREW_RE = /[֐-׿]/;
export const containsHebrew = (text = '') => HEBREW_RE.test(text);

export const resolveDirection = (field) => {
  const d = field.direction || 'auto';
  if (d === 'rtl' || d === 'ltr') return d;
  return containsHebrew(field.text) ? 'rtl' : 'ltr';
};

export const TEXT_DEFAULTS = {
  font_size: 24,
  font_family: 'Playfair Display',
  color: '#1A1A1A',
  text_align: 'center',
  line_spacing: null,
  font_weight: 'normal',
  font_style: 'normal',
  underline: false,
  strike: false,
  letter_spacing: 0,
  direction: 'auto',
  opacity: 1,
  rotation: 0,
  stroke_width: 0,
  stroke_color: '#FFFFFF',
  shadow_color: null,
  shadow_blur: 6,
  shadow_x: 0,
  shadow_y: 3,
  bg_color: null,
  bg_padding: 12,
  bg_radius: 8,
  highlight_color: null,
  hidden: false,
  locked: false,
};

export const ELEMENT_DEFAULTS = {
  x: 50,
  y: 50,
  w: 30,
  h: 10,
  rotation: 0,
  opacity: 1,
  fill: '#B09536',
  stroke_color: '#1A1A1A',
  stroke_width: 0,
  radius: 0,
  line_style: 'solid',
  fit: 'cover',
  corner_style: 'round',
  brightness: 1,
  contrast: 1,
  saturation: 1,
  sepia: 0,
  blur: 0,
  flip_h: false,
  flip_v: false,
  pos_x: 50,
  pos_y: 50,
  hidden: false,
  locked: false,
};

export const withTextDefaults = (field) => ({ ...TEXT_DEFAULTS, ...field });
export const withElementDefaults = (el) => ({
  ...ELEMENT_DEFAULTS,
  ...(el.type === 'line' ? { stroke_width: 2 } : {}),
  ...el,
});

/** Inline style for a text block drawn at `scale` (= rendered page width / 816). */
export const textFieldStyle = (rawField, scale) => {
  const f = withTextDefaults(rawField);
  const s = scale || 0;
  const decorations = [f.underline && 'underline', f.strike && 'line-through'].filter(Boolean).join(' ');
  const style = {
    position: 'absolute',
    left: `${f.x}%`,
    top: `${f.y}%`,
    transform: `translate(-50%, -50%)${f.rotation ? ` rotate(${f.rotation}deg)` : ''}`,
    fontFamily: f.font_family || 'inherit',
    fontSize: `${f.font_size * s}px`,
    fontWeight: f.font_weight === 'bold' ? 700 : 400,
    fontStyle: f.font_style === 'italic' ? 'italic' : 'normal',
    color: f.color,
    textAlign: f.text_align || 'center',
    direction: resolveDirection(f),
    unicodeBidi: 'plaintext',
    whiteSpace: f.line_width ? 'pre-wrap' : 'pre',
    width: 'max-content',
    maxWidth: f.line_width ? `${f.line_width}%` : undefined,
    lineHeight: f.line_spacing || 1.2,
    letterSpacing: f.letter_spacing ? `${f.letter_spacing * s}px` : undefined,
    textDecoration: decorations || undefined,
    textDecorationThickness: decorations ? `${Math.max(1, (f.font_size / 18) * s)}px` : undefined,
    opacity: f.opacity ?? 1,
    overflowWrap: 'break-word',
    display: f.hidden ? 'none' : undefined,
  };
  if (f.stroke_width > 0) {
    // Pillow draws the stroke outside the glyphs; CSS strokes are centred on
    // the outline, so double the width and paint the stroke under the fill.
    style.WebkitTextStroke = `${2 * f.stroke_width * s}px ${f.stroke_color}`;
    style.paintOrder = 'stroke fill';
  }
  if (f.shadow_color) {
    style.textShadow = `${f.shadow_x * s}px ${f.shadow_y * s}px ${f.shadow_blur * s}px ${f.shadow_color}`;
  }
  if (f.bg_color) {
    style.backgroundColor = f.bg_color;
    style.padding = `${f.bg_padding * s}px`;
    style.borderRadius = `${f.bg_radius * s}px`;
  }
  return style;
};

/**
 * "Highlight lines": a marker-pen band behind the lower 45% of every line.
 * Applied to an inline <span> wrapping the text so each wrapped line gets its
 * own band (box-decoration-break: clone). Returns null when the effect is off.
 */
export const highlightSpanStyle = (rawField) => {
  const color = rawField?.highlight_color;
  if (!color) return null;
  return {
    backgroundImage: `linear-gradient(transparent 55%, ${color} 55%)`,
    WebkitBoxDecorationBreak: 'clone',
    boxDecorationBreak: 'clone',
  };
};

/**
 * Background tint (`invitation.background`): an overlay over the background
 * image only, below every layer. Returns null when there is no tint.
 */
export const backgroundTintStyle = (background) => {
  if (!background || !background.tint_color) return null;
  const strength = Number(background.tint_strength);
  return {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background: background.tint_color,
    mixBlendMode: background.tint_mode === 'color' ? 'color' : 'multiply',
    opacity: Number.isFinite(strength) ? Math.min(1, Math.max(0, strength)) : 1,
  };
};

/** Inline style for an element's box drawn at `scale`. */
export const elementBoxStyle = (rawEl, scale) => {
  const el = withElementDefaults(rawEl);
  const s = scale || 0;
  const thickness = Math.max(0.5, el.stroke_width * s);
  const base = {
    position: 'absolute',
    left: `${el.x}%`,
    top: `${el.y}%`,
    width: `${el.w}%`,
    height: el.type === 'line' ? `${thickness}px` : `${el.h}%`,
    transform: `translate(-50%, -50%)${el.rotation ? ` rotate(${el.rotation}deg)` : ''}`,
    opacity: el.opacity ?? 1,
    display: el.hidden ? 'none' : undefined,
    boxSizing: 'border-box',
  };
  if (el.type === 'rect' && el.corner_style !== 'round' && el.radius > 0) {
    return base; // outline drawn as SVG by ElementView (cornerPoints)
  }
  if (el.type === 'rect' || el.type === 'ellipse') {
    return {
      ...base,
      background: el.fill || 'transparent',
      border: el.stroke_width > 0 ? `${thickness}px solid ${el.stroke_color}` : undefined,
      borderRadius: el.type === 'ellipse' ? '50%' : `${el.radius * s}px`,
    };
  }
  if (el.type === 'line') {
    if (el.line_style === 'dashed') {
      return {
        ...base,
        background: `repeating-linear-gradient(90deg, ${el.stroke_color} 0 ${3 * thickness}px, transparent ${3 * thickness}px ${5 * thickness}px)`,
      };
    }
    if (el.line_style === 'dotted') {
      return {
        ...base,
        background: `radial-gradient(circle, ${el.stroke_color} ${thickness / 2}px, transparent ${thickness / 2 + 0.5}px) 0 50% / ${2 * thickness}px ${thickness}px repeat-x`,
      };
    }
    return { ...base, background: el.stroke_color, borderRadius: `${thickness / 2}px` };
  }
  if (el.type === 'image') {
    if (el.corner_style !== 'round' && el.radius > 0) {
      const W = (el.w / 100) * PAGE_WIDTH * s;
      const H = (el.h / 100) * PAGE_HEIGHT * s;
      const pts = cornerPoints(W, H, el.corner_style, el.radius * s);
      return { ...base, overflow: 'hidden', clipPath: `polygon(${pts.map(([x, y]) => `${x.toFixed(2)}px ${y.toFixed(2)}px`).join(',')})` };
    }
    return { ...base, borderRadius: `${el.radius * s}px`, overflow: 'hidden' };
  }
  return base;
};

/** All drawable items (elements + text fields) in paint order. */
export const layersInOrder = (textFields = [], elements = []) => {
  const items = [
    ...elements.map((el, i) => ({ kind: 'element', item: el, z: el.z ?? 0, tie: 0, idx: i })),
    ...textFields.map((tf, i) => ({ kind: 'text', item: tf, z: tf.z ?? 100 + i, tie: 1, idx: i })),
  ];
  return items.sort((a, b) => a.z - b.z || a.tie - b.tie || a.idx - b.idx);
};

export const newId = (prefix) => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

// ---- Corner shapes (spec "Corners"; mirrored by corner_points() in backend invitation_render.py) ----
export const CORNER_STYLES = ['round', 'bevel', 'scoop', 'notch'];
const ARC_STEPS = 12;

/**
 * Outline of a w x h box whose corners are cut in `style` with size r, as [[x, y], ...] (clockwise).
 * round = convex quarter circle, bevel = straight cut, scoop = concave quarter circle, notch = square cut.
 */
export const cornerPoints = (w, h, style, r) => {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  if (rr <= 0) return [[0, 0], [w, 0], [w, h], [0, h]];
  const corners = [
    // [start, end, corner origin, inward signs]
    [[0, rr], [rr, 0], [0, 0], [1, 1]],
    [[w - rr, 0], [w, rr], [w, 0], [-1, 1]],
    [[w, h - rr], [w - rr, h], [w, h], [-1, -1]],
    [[rr, h], [0, h - rr], [0, h], [1, -1]],
  ];
  const pts = [];
  for (const [s, e, o, [sx, sy]] of corners) {
    if (style === 'bevel') {
      pts.push(s, e);
    } else if (style === 'notch') {
      pts.push(s, [o[0] + sx * rr, o[1] + sy * rr], e);
    } else {
      const c = style === 'scoop' ? o : [o[0] + sx * rr, o[1] + sy * rr];
      const a0 = Math.atan2(s[1] - c[1], s[0] - c[0]);
      let d = Math.atan2(e[1] - c[1], e[0] - c[0]) - a0;
      while (d <= -Math.PI) d += 2 * Math.PI;
      while (d > Math.PI) d -= 2 * Math.PI;
      for (let i = 0; i <= ARC_STEPS; i += 1) {
        const a = a0 + (d * i) / ARC_STEPS;
        pts.push([c[0] + rr * Math.cos(a), c[1] + rr * Math.sin(a)]);
      }
    }
  }
  return pts;
};

/** Inner edge of a scooped border: arcs around the ORIGINAL corners with radius r + d (concentric). */
const scoopInset = (w, h, r, d) => {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  const R = rr + d;
  const q = -d + Math.sqrt(Math.max(0, R * R - d * d));
  const W = w - 2 * d;
  const H = h - 2 * d;
  const corners = [
    [[0, q], [q, 0], [-d, -d]],
    [[W - q, 0], [W, q], [W + d, -d]],
    [[W, H - q], [W - q, H], [W + d, H + d]],
    [[q, H], [0, H - q], [-d, H + d]],
  ];
  const pts = [];
  for (const [s, e, c] of corners) {
    const a0 = Math.atan2(s[1] - c[1], s[0] - c[0]);
    let dd = Math.atan2(e[1] - c[1], e[0] - c[0]) - a0;
    while (dd <= -Math.PI) dd += 2 * Math.PI;
    while (dd > Math.PI) dd -= 2 * Math.PI;
    for (let i = 0; i <= ARC_STEPS; i += 1) {
      const a = a0 + (dd * i) / ARC_STEPS;
      pts.push([c[0] + R * Math.cos(a) + d, c[1] + R * Math.sin(a) + d]);
    }
  }
  return pts;
};

/** Same outline moved inward by d (the inside edge of a border of width d). */
export const insetCornerPoints = (w, h, style, r, d) => {
  if (style === 'scoop' && r > 0) return scoopInset(w, h, r, d);
  const inner = { round: r - d, bevel: r - d * 0.414, notch: r }[style] ?? r - d;
  return cornerPoints(Math.max(0, w - 2 * d), Math.max(0, h - 2 * d), style, Math.max(0, inner)).map(([x, y]) => [x + d, y + d]);
};

export const pointsToPath = (pts) => `M${pts.map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`).join('L')}Z`;

/** CSS filter for photo / background adjustments (same order as the server). */
export const adjustFilter = (o, scale) => {
  const parts = [];
  if ((o.brightness ?? 1) !== 1) parts.push(`brightness(${o.brightness})`);
  if ((o.contrast ?? 1) !== 1) parts.push(`contrast(${o.contrast})`);
  if ((o.saturation ?? 1) !== 1) parts.push(`saturate(${o.saturation})`);
  if (o.sepia) parts.push(`sepia(${o.sepia})`);
  if (o.blur) parts.push(`blur(${(o.blur * (scale || 1)).toFixed(2)}px)`);
  return parts.length ? parts.join(' ') : undefined;
};

export const flipTransform = (o) =>
  o.flip_h || o.flip_v ? `scale(${o.flip_h ? -1 : 1}, ${o.flip_v ? -1 : 1})` : undefined;

/** Filter / mirror for the template artwork (invitation.background adjustments; tint is a separate overlay). */
export const backgroundImageStyle = (background, scale) =>
  background ? { filter: adjustFilter(background, scale), transform: background.flip_h ? 'scaleX(-1)' : undefined } : {};
